import * as Sentry from '@sentry/react'
import { varrer } from './mascara'

/**
 * A ligação do portal com o Sentry (demanda 012 · `OBS-04`).
 *
 * 🔴 **O filtro vem antes do DSN, e não é ordem de leitura — é ordem de
 * execução.** A §3.2 da demanda: *"configurar o filtro primeiro, e só depois
 * ligar a produção; o que já subiu não se apaga com uma configuração nova"*.
 *
 * ⚠️ E aqui vale um cuidado que o back não tem: o portal é a tela do
 * **atendente**, e o que passa por ela é conversa de cliente. Uma mensagem de
 * erro que ecoe o corpo de uma resposta da API pode levar telefone, e-mail e
 * documento junto — por um caminho que ninguém audita.
 */

/**
 * Liga o Sentry **se houver DSN**. Sem DSN não liga e não reclama: é o estado
 * normal em desenvolvimento, e é o que permite este código subir antes de a
 * produção ser apontada.
 */
export function ligarSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) return

  Sentry.init({
    dsn,

    // O mesmo SHA que etiqueta a imagem no GHCR e que o `/saude` do back
    // devolve (critério 6). Sem ele, o source map não casa com o bundle.
    release: import.meta.env.VITE_VERSAO_SHA || 'local',
    environment: import.meta.env.VITE_SENTRY_AMBIENTE || 'production',

    // 🔴 Nunca: manda IP e identidade do usuário junto do evento.
    sendDefaultPii: false,

    // `OBS-07` (tracing e replay) ficou para a sustentação — e o replay seria
    // literalmente gravar a tela do atendente, com a conversa dentro.
    tracesSampleRate: 0,

    beforeSend(evento) {
      if (evento.message) evento.message = varrer(evento.message)

      for (const ex of evento.exception?.values ?? [])
        if (ex.value) ex.value = varrer(ex.value)

      // A URL pode levar a chave do link assinado do acervo em `?k=`, e a query
      // não serve para diagnosticar nada que a rota já não diga.
      if (evento.request) {
        delete evento.request.data
        delete evento.request.cookies
        delete evento.request.headers
        if (evento.request.url) evento.request.url = evento.request.url.split('?')[0]
      }

      if (evento.user) evento.user = {}

      for (const migalha of evento.breadcrumbs ?? [])
        if (migalha.message) migalha.message = varrer(migalha.message)

      return evento
    },
  })
}
